import {
	Component,
	inject,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { Location } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatBottomSheet } from '@angular/material/bottom-sheet';

import { ActivatedRoute, Router } from '@angular/router';
import { Loading } from '../../../applications/loading';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
	MomentDateAdapter,
} from '@angular/material-moment-adapter';
import {
	ReplaySubject,
	Subject,
	takeUntil,
} from 'rxjs';
import { CUSTOM_DATE_FORMAT } from '@lmc-app/shared/constants/common.constant';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { RoleService } from '@lmc-app/core/services/role.service';
import { PositionService } from '@lmc-app/core/services/position.service';
import { CompanyService } from '@lmc-app/core/services/company.service';
import { DepartmentService } from '@lmc-app/core/services/department.service';
import { EmployeeService } from '@lmc-app/core/services/employee.service';
import { RapidUserAccountService } from '@lmc-app/core/services/rapid-user-account.service';
import { MatSelectSearchUtil } from '@lmc-app/utils/mat-select-search.util';
import { genValidPass } from '@lmc-app/utils/password-functions'
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';

@Component({
	selector: 'app-create-account',
	templateUrl: './create-account.component.html',
	styleUrls: ['./create-account.component.scss'],
	providers: [
		{
			provide: DateAdapter,
			useClass: MomentDateAdapter,
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
		},
		{ provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMAT },
	],
})
export class CreateAccountComponent
	implements OnInit, OnDestroy {
	private readonly passwordRe: RegExp = /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[`~!@#$%\^&*\(\)\-_\+\=\[\]\{\}\\\|;:\'",\.\<\>?\/]).*?/;

	protected createAccountForm = new FormGroup({
		email: new FormControl(null, [
			Validators.required,
			Validators.minLength(3),
			Validators.maxLength(45),
			Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]{2,}$/),
		]),
		password: new FormControl(null, [
			Validators.required,
			Validators.minLength(12),
			Validators.maxLength(45),
			Validators.pattern(this.passwordRe),
		]),
		role_id: new FormControl(null, Validators.required),
		full_name: new FormControl(null, Validators.required),
		position: new FormControl(null),
		zoho_owner_id: new FormControl(null, [
			Validators.required,
		]),
		extension: new FormControl(null, [
			Validators.min(100),
			Validators.max(99999999),
			Validators.pattern(/^[0-9]+$/),
		]),
		company_id: new FormControl(null),
		department_id: new FormControl(null),
		create_rapid: new FormControl(null),
	});
	private users: IdName[] = [];
	private roles: IdName[] = [];
	private positions: IdName[] = [];
	private companies: IdName[] = [];
	private departments: IdName[] = [];
	protected usersFilter: FormControl<string | null> = new FormControl<string>('');
	protected filteredUsers: ReplaySubject<IdName[]> = new ReplaySubject<
		IdName[]
	>(1);
	protected rolesFilter: FormControl<string | null> = new FormControl<string>('');
	protected filteredRoles: ReplaySubject<IdName[]> = new ReplaySubject<
		IdName[]
	>(1);
	protected positionsFilter: FormControl<string | null> = new FormControl<string>('');
	protected filteredPositions: ReplaySubject<IdName[]> = new ReplaySubject<
		IdName[]
	>(1);
	protected companiesFilter: FormControl<string | null> = new FormControl<string>('');
	protected filteredCompanies: ReplaySubject<IdName[]> = new ReplaySubject<
		IdName[]
	>(1);
	protected departmentsFilter: FormControl<string | null> = new FormControl<string>('');
	protected filteredDepartments: ReplaySubject<IdName[]> = new ReplaySubject<
		IdName[]
	>(1);
	protected _onDestroy = new Subject<void>();
	private readonly _snackBar = inject(MatSnackBar);
	private readonly _bottomSheet = inject(MatBottomSheet);

	constructor(
		private _location: Location,
		private readonly apiService: LegacyApiService,
		private readonly roleService: RoleService,
		private readonly positionService: PositionService,
		private readonly companyService: CompanyService,
		private readonly departmentService: DepartmentService,
		private readonly employeeService: EmployeeService,
		private readonly rapidUserAccountService: RapidUserAccountService,
		private readonly router: Router,
		private readonly activatedRoute: ActivatedRoute,
	) { }

	ngOnInit() {
		this.initialData();

		this.usersFilter.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.users,
					searchValue,
					this.filteredUsers
				);
			});

		this.rolesFilter.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.roles,
					searchValue,
					this.filteredRoles
				);
			});

		this.rolesFilter.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.roles,
					searchValue,
					this.filteredRoles
				);
			});

		this.positionsFilter.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.positions,
					searchValue,
					this.filteredPositions
				);
			});

		this.companiesFilter.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.companies,
					searchValue,
					this.filteredCompanies
				);
			});

		this.departmentsFilter.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((searchValue) => {
				MatSelectSearchUtil.filterAllStringFields(
					this.departments,
					searchValue,
					this.filteredDepartments
				);
			});
	}

	initialData(): Promise<any> {
		this.createAccountForm.controls.password.setValue(genValidPass(12, true, true, true, this.passwordRe));
		return new Promise((resolve, reject) => {
			Promise.all([
				this.getUsers(),
				this.getRoles(),
				this.getPositions(),
				this.getCompanies(),
			])
				.then(() => {
					const params = {
						role_id: this.activatedRoute.snapshot?.queryParamMap.get('role_id'),
						position: this.activatedRoute.snapshot?.queryParamMap.get('position'),
						company_id: this.activatedRoute.snapshot?.queryParamMap.get('company_id'),
						department_id: this.activatedRoute.snapshot?.queryParamMap.get('department_id'),
					}
					if (params.role_id) this.createAccountForm.controls.role_id.setValue(Number(params.role_id));
					if (params.position) this.createAccountForm.controls.position.setValue(params.position);
					if (params.company_id) {
						this.createAccountForm.controls.company_id.setValue(Number(params.company_id));
						this.onCompanyChanged(this.createAccountForm.controls.company_id.value)
						if (params.department_id) this.createAccountForm.controls.department_id.setValue(Number(params.department_id));
					}
					resolve(true);
				})
				.catch(() => reject(false));
		});
	}

	backClicked() {
		this._location.back();
	}

	getUsers() {
		this.apiService.getZohoUsers().subscribe({
			next: (res) => {
				this.users = JSON.parse(res.message.body).users.map(({ id, full_name }: { id: number, full_name: string }) => ({
					id,
					name: full_name,
				}))
				this.filteredUsers.next(this.users.slice());
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

	getRoles() {
		this.roleService.getRoles().subscribe({
			next: (res) => {
				this.roles = res.map(({ role_id, description }: { role_id: number, description: string }) => ({
					id: role_id,
					name: description,
				}))
				this.filteredRoles.next(this.roles.slice());
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

	getPositions() {
		this.positionService.getPositions().subscribe({
			next: (res) => {
				this.positions = res.map(({ position }: { position: string }) => ({
					id: position,
					name: position,
				}))
				this.filteredPositions.next(this.positions.slice());
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

	getCompanies() {
		this.createAccountForm.controls.company_id.disable();
		this.createAccountForm.controls.department_id.disable();
		this.companyService.getCompanies().subscribe({
			next: (res) => {
				this.companies = res.map(({ company_id, company_name }: { company_id: number, company_name: string }) => ({
					id: company_id,
					name: company_name,
				}))
				this.createAccountForm.controls.company_id.enable();
				this.filteredCompanies.next(this.companies.slice());
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

	onCompanyChanged(companyId: number | null) {
		this.createAccountForm.controls.department_id.reset();
		this.getDepartments(companyId);
	}

	getDepartments(companyId: number | null) {
		this.createAccountForm.controls.department_id.disable();
		if (companyId) {
			this.departmentService.getDepartments(companyId).subscribe({
				next: (res) => {
					this.departments = res.map(({ department_id, department_name }: { department_id: number, department_name: string }) => ({
						id: department_id,
						name: department_name,
					}))
					this.createAccountForm.controls.department_id.enable();
					this.filteredDepartments.next(this.departments.slice());
				},
				error: (error) => {
					console.error(error);
				},
			});
		}
	}

	createAccount() {
		this._bottomSheet.open(Loading, {
			disableClose: true,
			data: { message: 'Creating Account' },
		});
		const employee = this.convertFormToJson();
		this.employeeService.createEmployee(employee).subscribe({
			next: (res) => {
				this._snackBar.open(
					`Account for ${res.full_name} is created.`,
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
				this._bottomSheet.dismiss();
				this.router.navigate([`/admin-settings/account`]);
			},
			error: (error) => {
				this._bottomSheet.dismiss();
				console.error(error);
				this._snackBar.open(
					(error.error && (error.error.message && (error.error.message.name || error.error.message))) || 'Error updating account.',
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);

			},
		});
		if (this.createAccountForm.controls.create_rapid.value) {
			this.rapidUserAccountService.createRapidUserAccount(employee).subscribe({
				next: (res) => {
					this._snackBar.open(
						`Rapid User Account for ${res.full_name} is created.`,
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
					this._bottomSheet.dismiss();
				},
				error: (error) => {
					this._bottomSheet.dismiss();
					console.error(error);
					this._snackBar.open(
						(error.error && (error.error.message && (error.error.message.name || error.error.message))) || 'Error updating account.',
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);

				},
			});
		};
	}

	convertFormToJson() {
		const newAccount = this.createAccountForm.getRawValue();
		newAccount["platform"] = "lmc";
		return newAccount;
	}

	ngOnDestroy() {
		this._onDestroy.next();
		this._onDestroy.complete();
	}
}
